//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { RouteAnchor } from '@components/RouteAnchor';
import Icon from '@components/atoms/icon/Icon';
import { BodyText } from '@components/atoms/text/Text';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import { IRightsOption } from '@legacy-components/rights-management/request-rights-model';
import Cuid from '@models/Cuid';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { ChooseMessageSelectBox } from '@uia/rights/choose-message/ChooseMessageSelectBox';
import { getIsManualRightsConfigurationEnabled } from '@uia/rights/selectors/getUIARights';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

const ALLOWED_STATUSES = [
  RIGHTS_STATUS.FAILED_TO_SEND,
  RIGHTS_STATUS.INITIATED,
  RIGHTS_STATUS.NONE,
  RIGHTS_STATUS.PENDING,
  RIGHTS_STATUS.QUEUED,
  RIGHTS_STATUS.UNSENT,
  RIGHTS_STATUS.CANCELLED,
];

export interface ChooseMessageSelectProps {
  cuid: Cuid;
  requestType: RightsRequestOptions;
  selectedOption: IRightsOption | undefined;
  setSelectedOption: (selectedOption: IRightsOption) => void;
}

export const ChooseMessageSelect = ({
  cuid,
  requestType,
  selectedOption,
  setSelectedOption,
}: ChooseMessageSelectProps) => {
  const isManualRightsConfigurationEnabled = useSelector(
    getIsManualRightsConfigurationEnabled,
  );

  const {
    data: metadata,
    isError: isErrorMetadata,
    isLoading: isLoadingMetadata,
  } = useMediaMetadataQuery(cuid);

  if (isLoadingMetadata || isErrorMetadata) {
    return null;
  }

  const {
    rightsRequestStatus: { status },
  } = metadata;

  const isStatusAllowed = ALLOWED_STATUSES.includes(status);
  if (!isStatusAllowed || !isManualRightsConfigurationEnabled) {
    return null;
  }

  return (
    <Wrapper>
      <ChooseMessageSelectBox
        cuid={cuid}
        requestType={requestType}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
      />
      <SmallTooltip
        content={
          <BodyText>
            Choose a messaging campaign to pull text from. To edit existing
            messages or create new ones, go to{' '}
            <RouteAnchor
              state='account-settings.rights'
              target='_blank'
              trackingId={UIATrackingIds.RIGHTS_OPEN_SETTINGS_LINK}
            >
              Settings &gt; Rights Requests &gt; Configure
            </RouteAnchor>
          </BodyText>
        }
        target={<StyledIcon name='info' />}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${theme.spacings[3]};
  `,
);

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    color: ${theme.colors.aliases.bodySecondary};
  `,
);
